import cn from 'classnames'
import type { ReactNode } from 'react'
import React from 'react'

import { testProps } from 'utils/test/qaData'

import Tooltip from '../Dropdown/Tooltip'
import type { KitDimensions } from '../KitTypes'
import { KitSize } from '../KitTypes'
import type { KitIndents } from '../KitTypes'
import * as s from './Typography.module.scss'

const DEFAULT_WEIGHT = {
  base: 'regular',
  hint: 'regular',
  heading: 'medium',
  link: 'medium',
  placeholder: 'regular',
}

type HTMLSpan = Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'defaultValue' | 'onChange' | 'name' | 'onBlur' | 'onFocus'
>

export interface ITypographyProps extends HTMLSpan {
  children: ReactNode
  name?: string
  styleType?: 'base' | 'heading' | 'link' | 'hint' | 'placeholder'
  size?: KitSize | number
  weight?: 'regular' | 'medium' | 'semi' | 'bold'
  textAlign?: 'left' | 'center' | 'right'
  color?: string
  rows?: number
  tooltip?: React.ReactNode
  fluid?: boolean
  indents?: KitIndents
  sizes?: KitDimensions
  placeholder?: string
  onBlur?: (e: React.FocusEvent) => void
  onFocus?: (e: React.FocusEvent) => void
  focused?: boolean
  wordBreak?: React.CSSProperties['wordBreak']
  cursor?: React.CSSProperties['cursor']
  alignSelf?: React.CSSProperties['alignSelf']
  shrink?: React.CSSProperties['flexShrink']
  grow?: React.CSSProperties['flexGrow']
  width?: React.CSSProperties['width']
  minWidth?: React.CSSProperties['minWidth']
}

const Typography = React.forwardRef<HTMLSpanElement, ITypographyProps>(
  (
    {
      children,
      name,
      styleType = 'base',
      size = KitSize.M,
      weight,
      color,
      rows: WebkitLineClamp,
      tooltip,
      fluid,
      wordBreak,
      indents,
      placeholder,
      sizes,
      textAlign = 'left',
      focused,
      cursor,
      shrink,
      alignSelf,
      width,
      grow,
      minWidth,
      ...rest
    },
    ref,
  ) => {
    const sizeCn = typeof size === 'number' ? undefined : s[size]
    const sizeStyle = typeof size === 'number' ? { fontSize: size } : undefined
    const classnames = cn(
      s.root,
      s[styleType],
      sizeCn,
      s[`weight-${weight || DEFAULT_WEIGHT[styleType]}`],
      rest.onClick && [s.pointer],
      placeholder && [s.editablePlaceholder],
      fluid && [s.fluid],
    )

    return (
      <Tooltip fluid={fluid} mouseEnterDelay={1} name={name} overlay={tooltip}>
        <span
          {...testProps({ el: 'typography', name, focus: focused })}
          {...rest}
          className={classnames}
          data-placeholder={placeholder}
          ref={ref}
          style={{
            ...rest.style,
            ...indents,
            ...sizeStyle,
            cursor,
            WebkitLineClamp,
            wordBreak,
            color,
            textAlign,
            alignSelf,
            flexShrink: shrink,
            flexGrow: grow,
            minWidth,
            width,
          }}
        >
          {children}
        </span>
      </Tooltip>
    )
  },
)

Typography.displayName = 'Typography'
export default Typography
